import { z } from 'zod';
import type { BootstrapData, PaginatedResponse } from '@/types';
import { buildLoginRedirectHref } from '@/lib/auth-redirect';
import { isPublicAuthPath } from '@/lib/site-paths';

let currentNonce = typeof window !== 'undefined' ? window.__CP_BOOTSTRAP__?.nonce ?? '' : '';

export function setNonce(nonce: string): void {
  currentNonce = nonce;
}

function getBootstrap(): BootstrapData {
  if (typeof window !== 'undefined' && window.__CP_BOOTSTRAP__) {
    return window.__CP_BOOTSTRAP__;
  }

  return { nonce: '', isLoggedIn: false, user: null, apiBase: '', siteUrl: '' };
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export class ApiContractError extends ApiError {
  constructor(target: 'request' | 'response', data: unknown) {
    super(`API ${target} validation failed.`, target === 'request' ? 400 : 500, data);
    this.name = 'ApiContractError';
  }
}

export type ForbiddenBehavior = 'redirect' | 'throw';
type ApiParams = Record<string, string | number | boolean | undefined>;

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: ApiParams;
  onForbidden?: ForbiddenBehavior;
  skipNonce?: boolean;
  requestSchema?: z.ZodTypeAny;
  responseSchema?: z.ZodTypeAny;
}

function parseWithSchema<T>(schema: z.ZodTypeAny | undefined, value: unknown, target: 'request' | 'response'): T {
  if (!schema) {
    return value as T;
  }

  const result = schema.safeParse(value);
  if (!result.success) {
    throw new ApiContractError(target, result.error.flatten());
  }

  return result.data as T;
}

function buildUrl(endpoint: string, params?: ApiParams): string {
  const { apiBase } = getBootstrap();
  const normalizedBase = apiBase.replace(/\/+$/, '');
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = new URL(`${normalizedBase}${normalizedEndpoint}`, window.location.origin);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

function isPublicAuthPage(): boolean {
  return isPublicAuthPath(window.location.pathname);
}

async function redirectToLogin(): Promise<never> {
  try {
    const { useAuthStore } = await import('@/stores/auth-store');
    useAuthStore.getState().logout();
  } catch {
    // ignore
  }

  window.location.href = buildLoginRedirectHref(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
  throw new ApiError('Unauthorized – redirecting to login', 401);
}

async function handleForbidden(onForbidden: ForbiddenBehavior): Promise<void> {
  if (onForbidden === 'throw') {
    return;
  }

  if (!isPublicAuthPage()) {
    await redirectToLogin();
  }
}

async function parseApiError(response: Response): Promise<ApiError> {
  let data: unknown;
  let message = `API error: ${response.status}`;

  try {
    data = await response.json();
    if (
      typeof data === 'object' &&
      data !== null &&
      'message' in data &&
      typeof (data as { message?: unknown }).message === 'string'
    ) {
      message = (data as { message: string }).message;
    }
  } catch {
    // ignore
  }

  return new ApiError(message, response.status, data);
}

export async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    params,
    onForbidden = 'redirect',
    skipNonce = false,
    requestSchema,
    responseSchema,
  } = options;

  const headers: Record<string, string> = {};
  if (!skipNonce) {
    headers['X-WP-Nonce'] = currentNonce;
  }

  const isFormData = body instanceof FormData;
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(buildUrl(endpoint, params), {
    method,
    headers,
    credentials: 'include',
    body: body === undefined || method === 'GET'
      ? undefined
      : isFormData
        ? body
        : JSON.stringify(parseWithSchema(requestSchema, body, 'request')),
  });

  if (response.status === 401 || response.status === 403) {
    await handleForbidden(onForbidden);
  }

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const json = await response.json();
  return parseWithSchema<T>(responseSchema, json, 'response');
}

export async function apiWithPagination<T>(
  endpoint: string,
  params?: ApiParams,
  options: Omit<ApiOptions, 'method' | 'body' | 'params'> = {},
): Promise<PaginatedResponse<T>> {
  const { responseSchema, onForbidden = 'redirect' } = options;
  const response = await fetch(buildUrl(endpoint, params), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': currentNonce,
    },
    credentials: 'include',
  });

  if (response.status === 401 || response.status === 403) {
    await handleForbidden(onForbidden);
  }

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const data = (await response.json()) as T[];
  const payload: PaginatedResponse<T> = {
    data,
    total: Number.parseInt(response.headers.get('X-WP-Total') ?? '0', 10),
    totalPages: Number.parseInt(response.headers.get('X-WP-TotalPages') ?? '0', 10),
  };

  return parseWithSchema<PaginatedResponse<T>>(responseSchema, payload, 'response');
}
